import React, { useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';

import { showConfirmDialogComponent } from '../../core/ConfirmDialog';

import { useAlert } from '../../context/AlertContext';
import { useGetBookingOptions } from '../../hooks/reactQuerys/MeetingRequestQueries';
import { IMeetingType, meetingTypeSchema } from '../../Interfaces/IMeetingType';
import {
  useCreateMeetingType,
  useDeleteMeetingType,
  useGetMeetingTypes,
  useUpdateMeetingType,
} from '../../hooks/reactQuerys/MeetingTypeQueries';
import { useGetLocations } from '../../hooks/reactQuerys/LocationQueries';
import { useGetRooms } from '../../hooks/reactQuerys/RoomQueries';
import { useGetUsers } from '../../hooks/reactQuerys/UserQueries';
import { useCalendarConnections } from '../../hooks/reactQuerys/CalendarConnectionQueries';

const SLOT_STEP_OPTIONS = [15, 30, 60];

const emptyMeetingType = (): IMeetingType => meetingTypeSchema.parse({});

const numberOrNull = (value: string): number | null =>
  value.trim() === '' ? null : Number(value);

export const MeetingTypeManager: React.FC = () => {
  const theme = useTheme();
  const { showAlert } = useAlert();
  const { bookingOptions } = useGetBookingOptions();
  const { meetingTypes, isLoadingMeetingTypes, refetchMeetingTypes } =
    useGetMeetingTypes();
  const { createMeetingType, isPendingCreateMeetingType } =
    useCreateMeetingType();
  const { updateMeetingType, isPendingUpdateMeetingType } =
    useUpdateMeetingType();
  const { deleteMeetingType, isPendingDeleteMeetingType } =
    useDeleteMeetingType();
  const { locations } = useGetLocations();
  const { rooms } = useGetRooms();
  const { users } = useGetUsers();

  const [edit, setEdit] = useState<IMeetingType | null>(null);
  const [viewObject, setViewObject] = useState<IMeetingType | null>(null);

  // Kalenderverbindungen des zuständigen Notars/SB (für die Free/Busy-Kopplung).
  const { connections } = useCalendarConnections(edit?.idUser ?? 0);
  // Eigene Kalenderverbindungen für die Read-only-Ansicht (kann ein anderer
  // Notar/Mitarbeiter sein als der gerade im Bearbeiten-Dialog).
  const { connections: viewConnections } = useCalendarConnections(
    viewObject?.idUser ?? 0
  );

  const busy =
    isPendingCreateMeetingType ||
    isPendingUpdateMeetingType ||
    isPendingDeleteMeetingType;

  const roomsForLocation = useMemo(
    () =>
      edit?.idLocation
        ? rooms.filter(room => room.Location_idLocation === edit.idLocation)
        : [],
    [rooms, edit?.idLocation]
  );

  const locationName = (idLocation: number | null): string =>
    locations?.find(location => location.idLocation === idLocation)?.Location ??
    '–';

  // Name-Auflösung für die Read-only-Ansicht (nutzt dieselben Listen wie der
  // Bearbeiten-Dialog).
  const roomName = (idRoom: number | null): string =>
    (idRoom !== null && rooms?.find(room => room.idRoom === idRoom)?.Name) ||
    '—';

  const userName = (idUser: number | null): string => {
    const user = idUser !== null && users?.find(u => u.idUser === idUser);
    return user ? `${user.FirstName} ${user.LastName}` : '—';
  };

  const connectionLabel = (idConnection: number | null): string =>
    (idConnection !== null &&
      viewConnections?.find(
        connection => connection.idUserCalendarConnection === idConnection
      )?.Label) ||
    '—';

  const patch = (changes: Partial<IMeetingType>) =>
    setEdit(current => (current ? { ...current, ...changes } : current));

  const isInvalid = (mt: IMeetingType): string | null => {
    if (mt.Name.trim() === '') {
      return 'Bitte einen Namen angeben.';
    }
    if (!mt.DurationInMin || mt.DurationInMin <= 0) {
      return 'Die Dauer muss größer als 0 sein.';
    }
    if (mt.BufferBeforeMin < 0 || mt.BufferAfterMin < 0) {
      return 'Pufferzeiten dürfen nicht negativ sein.';
    }
    if (mt.SlotStepInMin !== null && mt.SlotStepInMin <= 0) {
      return 'Das Buchungsraster muss leer oder größer als 0 sein.';
    }
    if (mt.MinLeadTimeMinutes !== null && mt.MinLeadTimeMinutes < 0) {
      return 'Der Mindestvorlauf darf nicht negativ sein.';
    }
    if (mt.BookingHorizonDays !== null && mt.BookingHorizonDays <= 0) {
      return 'Der Buchungshorizont muss leer oder größer als 0 sein.';
    }
    if (mt.idRoom && !mt.idLocation) {
      return 'Für einen Standard-Raum muss ein Standort gewählt sein.';
    }
    if (
      mt.idRoom &&
      !rooms.some(
        room =>
          room.idRoom === mt.idRoom &&
          room.Location_idLocation === mt.idLocation
      )
    ) {
      return 'Der Raum gehört nicht zum gewählten Standort.';
    }
    return null;
  };

  const handleSave = async () => {
    if (!edit) {
      return;
    }
    const error = isInvalid(edit);
    if (error) {
      showAlert({ severity: 'error', text: error });
      return;
    }
    try {
      // Nur noch ein Schalter „Aktiv": aktive Terminart = öffentlich buchbar.
      // IsBookablePublic wird gleich IsActive gehalten (Backend-Filter nutzt es).
      const payload = { ...edit, IsBookablePublic: edit.IsActive };
      if (payload.idMeetingType < 0) {
        await createMeetingType(payload);
      } else {
        await updateMeetingType(payload);
      }
      showAlert({ severity: 'success', text: 'Terminart gespeichert.' });
      setEdit(null);
      void refetchMeetingTypes();
    } catch {
      showAlert({ severity: 'error', text: 'Speichern fehlgeschlagen.' });
    }
  };

  const handleDelete = async () => {
    if (!edit || edit.idMeetingType < 0) {
      setEdit(null);
      return;
    }
    const confirmed = await showConfirmDialogComponent(
      'Terminart löschen',
      `Terminart „${edit.Name}" wirklich löschen? Das kann nicht rückgängig gemacht werden.`,
      'Löschen',
      'Abbrechen',
      theme
    );
    if (!confirmed) {
      return;
    }
    try {
      await deleteMeetingType(edit.idMeetingType);
      showAlert({ severity: 'success', text: 'Terminart gelöscht.' });
      setEdit(null);
      void refetchMeetingTypes();
    } catch {
      showAlert({ severity: 'error', text: 'Löschen fehlgeschlagen.' });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ mb: 2 }}
      >
        <Typography variant='h6'>Terminarten</Typography>
        <Button variant='contained' onClick={() => setEdit(emptyMeetingType())}>
          Neue Terminart
        </Button>
      </Stack>

      {isLoadingMeetingTypes && <CircularProgress size={24} />}

      {!isLoadingMeetingTypes && meetingTypes.length === 0 && (
        <Typography color='text.secondary'>
          Noch keine Terminarten konfiguriert.
        </Typography>
      )}

      {meetingTypes.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Dauer</TableCell>
              <TableCell>Raster</TableCell>
              <TableCell>Standort</TableCell>
              <TableCell>Aktiv</TableCell>
              <TableCell align='right'>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {meetingTypes.map(mt => (
              <TableRow
                key={mt.idMeetingType}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => setEdit(meetingTypeSchema.parse(mt))}
              >
                <TableCell>{mt.Name}</TableCell>
                <TableCell>{mt.DurationInMin} Min.</TableCell>
                <TableCell>
                  {mt.SlotStepInMin ? `${mt.SlotStepInMin} Min.` : 'Standard'}
                </TableCell>
                <TableCell>{locationName(mt.idLocation)}</TableCell>
                <TableCell>{mt.IsActive ? 'Ja' : 'Nein'}</TableCell>
                <TableCell align='right'>
                  <IconButton
                    size='small'
                    aria-label='Ansehen'
                    onClick={event => {
                      event.stopPropagation();
                      setViewObject(meetingTypeSchema.parse(mt));
                    }}
                  >
                    <Visibility fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={edit !== null}
        onClose={() => setEdit(null)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>
          {edit && edit.idMeetingType < 0
            ? 'Neue Terminart'
            : 'Terminart bearbeiten'}
        </DialogTitle>
        {edit && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {edit.IsActive &&
                edit.idMeetingType > 0 &&
                !bookingOptions.some(
                  o => o.idMeetingType === edit.idMeetingType
                ) && (
                  <Alert severity='warning'>
                    Diese Terminart ist aktiv, hat aber keine buchbaren Zeiten.
                    Bitte unter <strong>Buchbare Zeiten</strong> Zeitfenster für
                    diese Terminart (und Standort) anlegen — sonst ist sie im
                    Mandantenportal nicht wählbar.
                  </Alert>
                )}
              <TextField
                label='Name'
                value={edit.Name}
                onChange={event => patch({ Name: event.target.value })}
                required
                fullWidth
                size='small'
              />
              <Stack direction='row' spacing={2}>
                <TextField
                  label='Dauer (Min.)'
                  type='number'
                  value={edit.DurationInMin}
                  onChange={event =>
                    patch({ DurationInMin: Number(event.target.value) })
                  }
                  size='small'
                  fullWidth
                />
                <FormControl size='small' fullWidth>
                  <InputLabel id='slot-step-label'>Buchungsraster</InputLabel>
                  <Select
                    labelId='slot-step-label'
                    label='Buchungsraster'
                    value={
                      edit.SlotStepInMin === null
                        ? ''
                        : String(edit.SlotStepInMin)
                    }
                    onChange={event =>
                      patch({ SlotStepInMin: numberOrNull(event.target.value) })
                    }
                  >
                    <MenuItem value=''>Standard (15 Min.)</MenuItem>
                    {SLOT_STEP_OPTIONS.map(step => (
                      <MenuItem key={step} value={String(step)}>
                        {step} Minuten
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction='row' spacing={2}>
                <TextField
                  label='Puffer vorher (Min.)'
                  type='number'
                  value={edit.BufferBeforeMin}
                  onChange={event =>
                    patch({ BufferBeforeMin: Number(event.target.value) })
                  }
                  size='small'
                  fullWidth
                />
                <TextField
                  label='Puffer nachher (Min.)'
                  type='number'
                  value={edit.BufferAfterMin}
                  onChange={event =>
                    patch({ BufferAfterMin: Number(event.target.value) })
                  }
                  size='small'
                  fullWidth
                />
              </Stack>
              <Stack direction='row' spacing={2}>
                <TextField
                  label='Buchungshorizont (Tage)'
                  type='number'
                  value={edit.BookingHorizonDays ?? ''}
                  onChange={event =>
                    patch({
                      BookingHorizonDays: numberOrNull(event.target.value),
                    })
                  }
                  helperText='Leer = Standard (14)'
                  size='small'
                  fullWidth
                />
              </Stack>

              <FormControl size='small' fullWidth>
                <InputLabel id='mt-location-label'>
                  Standard-Standort
                </InputLabel>
                <Select
                  labelId='mt-location-label'
                  label='Standard-Standort'
                  value={
                    edit.idLocation === null ? '' : String(edit.idLocation)
                  }
                  onChange={event =>
                    patch({
                      idLocation: numberOrNull(event.target.value),
                      idRoom: null,
                    })
                  }
                >
                  <MenuItem value=''>— kein Standort —</MenuItem>
                  {(locations ?? []).map(location => (
                    <MenuItem
                      key={location.idLocation}
                      value={String(location.idLocation)}
                    >
                      {location.Location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size='small' fullWidth disabled={!edit.idLocation}>
                <InputLabel id='mt-room-label'>Standard-Raum</InputLabel>
                <Select
                  labelId='mt-room-label'
                  label='Standard-Raum'
                  value={edit.idRoom === null ? '' : String(edit.idRoom)}
                  onChange={event =>
                    patch({ idRoom: numberOrNull(event.target.value) })
                  }
                >
                  <MenuItem value=''>— kein Raum —</MenuItem>
                  {roomsForLocation.map(room => (
                    <MenuItem key={room.idRoom} value={String(room.idRoom)}>
                      {room.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size='small' fullWidth>
                <InputLabel id='mt-user-label'>
                  Zuständiger Notar/Mitarbeiter
                </InputLabel>
                <Select
                  labelId='mt-user-label'
                  label='Zuständiger Notar/Mitarbeiter'
                  value={edit.idUser === null ? '' : String(edit.idUser)}
                  onChange={event =>
                    patch({
                      idUser: numberOrNull(event.target.value),
                      // Kalenderwahl gehört zum Notar -> bei Wechsel zurücksetzen.
                      idUserCalendarConnection: null,
                    })
                  }
                >
                  <MenuItem value=''>— keiner —</MenuItem>
                  {(users ?? []).map(user => (
                    <MenuItem key={user.idUser} value={String(user.idUser)}>
                      {user.FirstName} {user.LastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size='small' fullWidth disabled={!edit.idUser}>
                <InputLabel id='mt-cal-label'>Kalenderverbindung</InputLabel>
                <Select
                  labelId='mt-cal-label'
                  label='Kalenderverbindung'
                  value={
                    edit.idUserCalendarConnection === null
                      ? ''
                      : String(edit.idUserCalendarConnection)
                  }
                  onChange={event =>
                    patch({
                      idUserCalendarConnection: numberOrNull(
                        event.target.value
                      ),
                    })
                  }
                >
                  <MenuItem value=''>— Standardkalender / keine —</MenuItem>
                  {connections.map(connection => (
                    <MenuItem
                      key={connection.idUserCalendarConnection}
                      value={String(connection.idUserCalendarConnection)}
                    >
                      {connection.Label}
                      {connection.Provider ? ` (${connection.Provider})` : ''}
                      {connection.IsActive ? '' : ' – inaktiv'}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {edit.idUser
                    ? 'Termine dieser Art werden mit diesem Kalender abgeglichen (Free/Busy).'
                    : 'Erst einen zuständigen Notar/Mitarbeiter wählen.'}
                </FormHelperText>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={edit.IsActive}
                    onChange={event =>
                      patch({ IsActive: event.target.checked })
                    }
                  />
                }
                label='Aktiv (im Mandantenportal buchbar)'
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={edit.AllowParallel}
                    onChange={event =>
                      patch({ AllowParallel: event.target.checked })
                    }
                  />
                }
                label='Darf parallel laufen (Kalender wird ignoriert)'
              />

              {edit.IsActive &&
                !edit.AllowParallel &&
                edit.idUserCalendarConnection === null && (
                  <Alert severity='warning'>
                    Ohne Kalenderverbindung werden externe Kalendertermine nicht
                    berücksichtigt — es kann zu Doppelbuchungen kommen. Wähle
                    oben eine Kalenderverbindung oder aktiviere „Darf parallel
                    laufen".
                  </Alert>
                )}
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          {edit && edit.idMeetingType > 0 && (
            <Button
              color='error'
              onClick={() => void handleDelete()}
              disabled={busy}
            >
              Löschen
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setEdit(null)} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={() => void handleSave()}
            disabled={busy}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewObject !== null}
        onClose={() => setViewObject(null)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Terminart ansehen</DialogTitle>
        {viewObject && (
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {[
                ['Name', viewObject.Name || '—'],
                ['Dauer (Min.)', String(viewObject.DurationInMin)],
                ['Puffer vorher (Min.)', String(viewObject.BufferBeforeMin)],
                ['Puffer nachher (Min.)', String(viewObject.BufferAfterMin)],
                [
                  'Buchungsraster (Min.)',
                  viewObject.SlotStepInMin === null
                    ? '—'
                    : String(viewObject.SlotStepInMin),
                ],
                [
                  'Buchungshorizont (Tage)',
                  viewObject.BookingHorizonDays === null
                    ? '—'
                    : String(viewObject.BookingHorizonDays),
                ],
                ['Standard-Standort', locationName(viewObject.idLocation)],
                ['Standard-Raum', roomName(viewObject.idRoom)],
                ['Zuständiger Notar/Mitarbeiter', userName(viewObject.idUser)],
                [
                  'Kalenderverbindung',
                  connectionLabel(viewObject.idUserCalendarConnection),
                ],
                ['Aktiv', viewObject.IsActive ? 'Ja' : 'Nein'],
                [
                  'Darf parallel laufen',
                  viewObject.AllowParallel ? 'Ja' : 'Nein',
                ],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography variant='caption' color='text.secondary'>
                    {label}
                  </Typography>
                  <Typography variant='body1'>{value}</Typography>
                </Box>
              ))}
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setViewObject(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeetingTypeManager;
